<template>
    <section class="contact-emails">
        <div class="emails-header">
            <h3>Emails</h3>

            <ds-filled-button
                data-qa="email-broadcast"
                @click="launchBardEmailBroadcast(contact.email)"
            >
                Send email
            </ds-filled-button>
        </div>

        <div class="status-type">
            <span v-for="(type, i) in statusType" :key="i" class="status-block">
                <span class="status-text" :style="{ backgroundColor: type.backgroundColor }" />{{ type.name }}
            </span>
        </div>

        <template v-if="isContactHasEmails" lang="html">
            <div class="table-scroller">
                <ds-table-grid
                    ref="table"
                    :data="emailsList"
                    align="center"
                    data-qa="email-list-table"
                    class="email-list-table"
                    :sort-field="sortField"
                    :sort-ascending="sortAscending"
                    @sort="sortTable"
                >
                    <ds-table-grid-column
                        label="Date sent"
                        prop="dateSent"
                        width="200"
                        sortable
                    >
                        <template slot-scope="item">
                            <span> {{ item.dateSent }} </span>
                        </template>
                    </ds-table-grid-column>

                    <ds-table-grid-column
                        label="Status"
                        width="160"
                        prop="status"
                    >
                        <template slot-scope="item">
                            <span class="status-block">
                                <span class="status-text" :style="{ backgroundColor: item.borderColor }" />{{ item.status }}
                            </span>
                        </template>
                    </ds-table-grid-column>

                    <ds-table-grid-column
                        label="Subject"
                        width="300"
                        prop="subject"
                    >
                        <template slot-scope="item">
                            <span> {{ item.subject }} </span>
                        </template>
                    </ds-table-grid-column>

                    <ds-table-grid-column
                        label="To Address"
                        width="200"
                        prop="toAddress"
                    >
                        <template slot-scope="item">
                            <span> {{ item.toAddress }} </span>
                        </template>
                    </ds-table-grid-column>

                    <ds-table-grid-column
                        label="Sender"
                        width="200"
                        prop="sender"
                    >
                        <template slot-scope="item">
                            <span> {{ item.sender }} </span>
                        </template>
                    </ds-table-grid-column>
                </ds-table-grid>
            </div>
        </template>

        <div v-if="!isContactHasEmails" class="no-emails-section">
            This Contact does not have any Email History
        </div>

        <ds-infinite-scroll
            v-if="isContactHasEmails"
            :limit="limit"
            :offset="offset"
            :loading="loading"
            :no-more-data="noMoreData"
            data-qa="emaillist-infinite-scroll"
            @load="handleInfiniteScrollLoad"
        >
            <ds-placeholder
                v-for="i in 3"
                :key="i"
                :rows="placeholder"
                class="placeholder"
            />
        </ds-infinite-scroll>
    </section>
</template>

<script>
import axios from 'axios';

export default {
    props: {
        contact: Object,
        id: [String, Number],
    },

    data() {
        return {
            url: '/app/emailHistory',
            limit: 20,
            offset: 0,
            loading: true,
            emailsList: [],
            noMoreData: false,
            sortField: 'dateSent',
            sortAscending: false,
            isBardBuilderEnabled: false,
            featureFlagValues: [],
            placeholder: [
                { height: '1rem', boxes: [2, 1.6, 3, 2, 2] },
            ],
            statusType: [
                {
                    name: 'Received',
                    backgroundColor: '#4a4a57',
                },
                {
                    name: 'Sent',
                    backgroundColor: '#d6d6d8',
                },
                {
                    name: 'Campaign',
                    backgroundColor: '#36a635',
                },
                {
                    name: 'Notification',
                    backgroundColor: '#fcbc00',
                },
                {
                    name: 'Bounced',
                    backgroundColor: '#e02500',
                },
            ],
        };
    },

    created() {
        this.loadEmails();
    },

    computed: {
        isContactHasEmails() {
            return Boolean(this.emailsList.length);
        },
    },

    methods: {
        async loadEmails() {
            try {
                const params = `offset=${this.offset}&limit=${this.limit}`;
                const onScrollEmails = await axios.get(`${this.url}/${this.contact.id}?${params}`);

                this.offset += onScrollEmails.data.length;
                this.emailsList.push(...onScrollEmails.data);

                if (onScrollEmails.data.length < this.limit) {
                    this.noMoreData = true;
                }
                this.getFeatureFlagValue();
            } catch (error) {
                this.$error({ message: 'Unable to get emails for this contact' });
            }
        },

        async handleInfiniteScrollLoad() {
            this.loading = true;
            await this.loadEmails();

            this.loading = false;
        },

        async getFeatureFlagValue() {
            try {
                const response = await axios.get('/app/broadcast/getFeatureflags');

                this.featureFlagValues = response.data;
                this.isBardBuilderEnabled = this.featureFlagValues.isBardBuilderEnabled;
            } catch (e) {
                this.$error({ message: 'Unable get featureFlag value' });
            }
        },

        async contactEmail(email) {
            try {
                const response = await axios.get('/app/contact/emailId');

                const contactEmailData = response.data;

                window.open(`/Mail/compose.jsp?id=${contactEmailData.mailId}&templateid=${
                    contactEmailData.templateId}&Mail0ToAddress_text=${email}&mergeid=${this.id}`);
            } catch (error) {
                this.$error({ message: 'error while loading email template details' });
            }
        },

        async launchBardEmailBroadcast(email) {
            const response = await axios.post(`/app/emailBroadcast/createListForContact?contactId=${this.contact.id}&emailAddress=${email}`);

            if (this.isBardBuilderEnabled) {
                window.location = `/app/email/broadcast/gallery?listId=${response.data}`;
            } else {
                this.contactEmail(email);
            }
        },

        sortTable({ ascending }) {
            const factor = ascending === true ? -1 : 1;

            this.sortAscending = ascending;
            this.emailsList = this.emailsList.sort((prev, next) => {
                if (prev.dateSent > next.dateSent) return -1 * factor;
                if (prev.dateSent < next.dateSent) return 1 * factor;

                return 0;
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.contact-emails {
    .emails-header {
        display: flex;
        justify-content: space-between;
        padding: $spacing-200 0;
        border-bottom: solid px-to-rem(1px) $color-gray-200;
        align-items: center;
    }

    .placeholder {
        margin: $spacing-200 0;
    }

    .status-block {
        display: flex;
        align-items: center;
    }

    .status-text {
        height: 0.5rem;
        width: 0.5rem;
        border-radius: 50%;
        margin-right: $spacing-100;
    }

    .status-type {
        display: flex;
        padding: $spacing-200;
        .status-block {
            margin-right: $spacing-200;
        }
    }

    .no-emails-section {
        font-size: 1.2rem;
        padding: $spacing-600;
        text-align: center;
        color: $color-gray-darker;
    }
}
</style>
